import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Pencil,
  Trash2,
  TrendingUp,
  UserPlus,
} from "lucide-react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import PageHeader from "@/components/shared/PageHeader";
import { StudentPicker } from "@/components/shared/StudentPicker";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { OpsDetailSection, OpsPersonCell } from "@/components/admin/ops-table";
import { formatDate } from "@/utils/formatters";
import {
  useAdminPlacements,
  useAdminStudents,
  useAdminPartners,
  useAdminPlacementSlots,
  useAdminPlacementDetail,
  useCreatePlacement,
  useDeletePlacement,
} from "@/hooks/useAdminData";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/admin/placements/")({
  component: AdminPlacementsPage,
});

function AdminPlacementsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState<any>(null);
  const [detailPlacementId, setDetailPlacementId] = useState<any>(null);

  // Assign dialog state
  const [assignStudent, setAssignStudent] = useState("");
  const [assignPartner, setAssignPartner] = useState("");
  const [assignSlot, setAssignSlot] = useState("");
  const [assignHours, setAssignHours] = useState("480");
  const [assignStartDate, setAssignStartDate] = useState("");
  const [assignEndDate, setAssignEndDate] = useState("");

  const { data: placementsData, isLoading } = useAdminPlacements();
  const deletePlacement = useDeletePlacement();
  const { data: students } = useAdminStudents();
  const { data: partners } = useAdminPartners();
  const { data: allSlots, isLoading: slotsLoading } = useAdminPlacementSlots();
  const { data: placementDetail, isLoading: detailLoading } =
    useAdminPlacementDetail(detailPlacementId);

  // A student who never finished onboarding has no profile name, and the API
  // returns that as an empty string. The email is the only field that always
  // identifies them, so fall back to it and mark the profile as incomplete.
  const identityOf = (row: any) => {
    const name = String(row?.studentName ?? row?.student_name ?? "").trim();
    const email = String(row?.studentEmail ?? row?.student_email ?? "").trim();
    return { name, email, incomplete: !name, label: name || email || "Unknown student" };
  };

  // The detail endpoint emits both camelCase and snake_case spellings.
  const detail: any = placementDetail ?? null;
  const detailStudent = identityOf(detail);
  const detailLogs: any[] = detail?.workLogs ?? detail?.work_logs ?? [];
  const detailRequired = detail?.hoursRequired ?? detail?.hours_required ?? 0;
  const detailLogged = detail?.hoursLogged ?? detail?.hours_logged ?? 0;
  const detailPending = detail?.hoursPending ?? detail?.hours_pending ?? 0;
  const detailProgress =
    detailRequired > 0 ? Math.min((detailLogged / detailRequired) * 100, 100) : 0;

  const placements = placementsData ?? [];
  const studentList = students ?? [];
  const partnerList = partners ?? [];
  const slotList = allSlots ?? [];

  // placement_slots.partner_id holds a users.id, not a partner_profiles.id,
  // so the slots endpoint has to be filtered with the partner's user_id.
  const selectedPartner: any = partnerList.find(
    (p: any) => String(p.id) === String(assignPartner),
  );

  const { data: filteredSlotsData } = useAdminPlacementSlots(
    selectedPartner?.user_id,
  );
  const filteredSlots = filteredSlotsData ?? [];

  const createPlacement = useCreatePlacement();

  const selectedSlotData = ((filteredSlots?.length ?? 0) > 0 ? filteredSlots : slotList)?.find(
    (s: any) => String(s.id) === String(assignSlot),
  );

  const resetAssignForm = () => {
    setAssignStudent("");
    setAssignPartner("");
    setAssignSlot("");
    setAssignHours("480");
    setAssignStartDate("");
    setAssignEndDate("");
  };

  const handleOpenAssign = () => {
    resetAssignForm();
    setAssignDialogOpen(true);
  };

  const handleSubmitAssign = async () => {
    if (!assignStudent || !assignPartner || !assignSlot || !assignHours || !assignStartDate) return;

    const startUnix = Math.floor(new Date(assignStartDate).getTime() / 1000);
    const endUnix = assignEndDate ? Math.floor(new Date(assignEndDate).getTime() / 1000) : 0;

    try {
      await createPlacement.mutateAsync({
        student_id: Number(assignStudent),
        partner_id: Number(selectedPartner?.user_id),
        slot_id: Number(assignSlot),
        start_date: startUnix,
        end_date: endUnix,
        hours_required: Number(assignHours),
      });
    } catch {
      // The mutation reports the failure; keep the dialog open with the entered
      // values so the admin can correct one field instead of retyping the form.
      return;
    }

    resetAssignForm();
    setAssignDialogOpen(false);
  };

  const availableStudents = studentList.filter(
    (s: any) => s.status === "active" || s.status === "pending",
  );
  const availablePartners = partnerList.filter((p: any) => p.status === "active");

  const filteredPlacements = placements.filter((p: any) => {
    const studentName = p.studentName || "";
    const partnerName = p.partnerName || "";
    const matchesSearch =
      studentName.toLowerCase().includes(search.toLowerCase()) ||
      partnerName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const avgProgress =
    placements.length > 0
      ? Math.round(
          placements.reduce((sum: number, p: any) => {
            const required = p.hoursRequired || 1; // avoid div by 0
            return sum + ((p.hoursLogged || 0) / required) * 100;
          }, 0) / placements.length,
        )
      : 0;

  const handleEdit = (placement: any) => {
    setSelectedPlacement(placement);
    setEditDialogOpen(true);
  };

  const handleDelete = (placement: any) => {
    setSelectedPlacement(placement);
    setDeleteDialogOpen(true);
  };

  const confirmTerminate = () => {
    const id = selectedPlacement?.id;
    if (!id) return;
    deletePlacement.mutate(id, { onSettled: () => setDeleteDialogOpen(false) });
  };


  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Placement operations"
        subtitle="Manage student-employer assignments, monitor lifecycle progress, and ensure compliance."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Placements" }]}
        action={
          <Button onClick={handleOpenAssign}>
            <UserPlus />
            New assignment
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard
          label="Total active assignments"
          value={placements.length}
          icon={<Briefcase />}
        />
        <StatCard
          label="Completed internships"
          value={placements.filter((p: any) => p.status === "completed").length}
          icon={<CheckCircle2 />}
        />
        <StatCard label="Average progress" value={`${avgProgress}%`} icon={<TrendingUp />} />
      </div>

      {/* Pipeline */}
      <SectionCard
        title="Active placement pipeline"
        description={`${filteredPlacements.length} of ${placements.length} placements`}
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by identity..."
              aria-label="Search placements by student or partner"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-full sm:w-[180px]" aria-label="Filter by status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Global status</SelectItem>
                <SelectItem value="active">Operational</SelectItem>
                <SelectItem value="completed">Fulfilled</SelectItem>
                <SelectItem value="pending">Awaiting deployment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : filteredPlacements.length === 0 ? (
          <EmptyState
            bare
            icon={<Briefcase />}
            title="No placements found"
            description={
              placements.length
                ? "Nothing matches this search or status."
                : "Create an assignment to start tracking a student placement."
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">Intern identity</TableHead>
                <TableHead>Corporate host</TableHead>
                <TableHead>Assigned role</TableHead>
                <TableHead>Operational progress</TableHead>
                <TableHead>Lifecycle status</TableHead>
                <TableHead className="pr-4 text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlacements.map((p: any) => {
                const required = p.hoursRequired || 1;
                const progress = ((p.hoursLogged || 0) / required) * 100;
                const student = identityOf(p);
                return (
                  <TableRow key={p.id}>
                    <TableCell className="min-w-56 py-1.5 pl-4">
                      <OpsPersonCell
                        name={student.label}
                        secondary={student.email && student.name ? student.email : undefined}
                        meta={
                          student.incomplete ? (
                            <Badge variant="warning" className="px-1 text-[10px] leading-4">
                              Incomplete profile
                            </Badge>
                          ) : undefined
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-foreground">
                      {p.partnerName}
                    </TableCell>
                    <TableCell className="text-body">{p.slot}</TableCell>
                    <TableCell className="w-52 min-w-44 py-1.5">
                      <div className="flex items-center gap-2.5">
                        <Progress
                          value={Math.min(progress, 100)}
                          className="flex-1"
                          aria-label="Deployment progress"
                        />
                        <span className="w-9 text-right font-mono text-caption text-foreground tabular-nums">
                          {Math.round(progress)}%
                        </span>
                      </div>
                      <p className="mt-0.5 font-mono text-[11px] text-muted-foreground tabular-nums">
                        {p.hoursLogged} / {p.hoursRequired} logged hours
                      </p>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={p.status} />
                    </TableCell>
                    <TableCell className="py-1.5 pr-3">
                      <div className="flex justify-end gap-0.5">
                        <IconAction
                          label="Update assignment"
                          icon={<Pencil />}
                          onClick={() => handleEdit(p)}
                        />
                        <IconAction
                          label="Terminate placement"
                          tone="destructive"
                          icon={<Trash2 />}
                          onClick={() => handleDelete(p)}
                        />
                        <IconAction
                          label={`View placement detail for ${identityOf(p).label}`}
                          icon={<ArrowRight />}
                          onClick={() => setDetailPlacementId(p.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* Edit placement dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Placement configuration</DialogTitle>
            <DialogDescription>Review the assignment details for this placement.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Student intern" className="sm:col-span-2">
              <Input defaultValue={selectedPlacement?.studentName} disabled />
            </Field>
            <Field label="Corporate host" className="sm:col-span-2">
              <Input defaultValue={selectedPlacement?.partnerName} disabled />
            </Field>
            <Field label="Assigned role">
              <Input defaultValue={selectedPlacement?.slot} />
            </Field>
            <Field label="Required hours">
              <Input type="number" defaultValue={selectedPlacement?.hoursRequired} />
            </Field>
            <Field label="Lifecycle status" className="sm:col-span-2">
              {({ id }) => (
                <Select defaultValue={selectedPlacement?.status || "active"}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Operational</SelectItem>
                    <SelectItem value="completed">Fulfilled</SelectItem>
                    <SelectItem value="pending">Awaiting deployment</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setEditDialogOpen(false)}>Update assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <AlertTriangle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Terminate placement</DialogTitle>
            <DialogDescription>
              Are you sure you want to terminate the placement for{" "}
              <span className="font-medium text-foreground">{selectedPlacement?.studentName}</span>{" "}
              at <span className="font-medium text-foreground">{selectedPlacement?.partnerName}</span>?
              You can restore it from Trash.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmTerminate}
              disabled={deletePlacement.isPending}
              loading={deletePlacement.isPending}
            >
              {!deletePlacement.isPending && <AlertTriangle />}
              {deletePlacement.isPending ? "Terminating…" : "Terminate now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New assignment dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(o) => !o && setAssignDialogOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Institutional placement assignment</DialogTitle>
            <DialogDescription>
              Authorize a new student placement within a partner organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Select student">
              {({ id, describedBy, invalid }) => (
                <StudentPicker
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  loading={isLoading}
                  value={assignStudent ? String(assignStudent) : null}
                  onChange={(v) => {
                    setAssignStudent(v ?? "");
                    setAssignSlot("");
                  }}
                  placeholder="Choose a student"
                  subtitle="Pick the student to place with a partner."
                  allGroupsLabel="All universities"
                  emptyText="No students available"
                  options={availableStudents.map((s: any) => ({
                    value: String(s.user_id ?? s.id),
                    label: s.name,
                    description: s.university || s.status,
                    group: s.university || undefined,
                  }))}
                />
              )}
            </Field>
            <Field label="Target partner organization">
              {({ id, describedBy, invalid }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={assignPartner ? String(assignPartner) : null}
                  onChange={(v) => {
                    setAssignPartner(v ?? "");
                    setAssignSlot("");
                  }}
                  placeholder="Choose a partner"
                  searchPlaceholder="Search partners…"
                  emptyText="No partners available"
                  options={availablePartners.map((p: any) => ({
                    value: String(p.id),
                    label: `${p.name} — ${p.industry}`,
                  }))}
                />
              )}
            </Field>
            <Field label="Available placement slot">
              {({ id, describedBy }) => (
                <Select
                  value={assignSlot ? String(assignSlot) : ""}
                  onValueChange={setAssignSlot}
                  disabled={!assignPartner}
                >
                  <SelectTrigger id={id} aria-describedby={describedBy}>
                    <SelectValue
                      placeholder={!assignPartner ? "Select partner first" : "Choose a slot"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {slotsLoading ? (
                      <p className="px-2 py-1.5 text-sm text-muted-foreground">Loading slots...</p>
                    ) : filteredSlots.length === 0 ? (
                      <p className="px-2 py-1.5 text-sm text-muted-foreground">
                        No published slots for this partner
                      </p>
                    ) : (
                      filteredSlots.map((slot: any) => (
                        <SelectItem key={slot.id} value={String(slot.id)}>
                          {slot.title} ({slot.filled_slots || 0}/{slot.max_students} filled)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Required field hours">
              <Input
                type="number"
                value={assignHours}
                onChange={(e) => setAssignHours(e.target.value)}
                min={1}
              />
            </Field>
            <Field label="Commencement date">
              <DatePicker
                value={assignStartDate}
                onChange={(v) => setAssignStartDate(v)}
                min={today}
              />
            </Field>
            <Field label="Conclusion date">
              <DatePicker
                value={assignEndDate}
                onChange={(v) => setAssignEndDate(v)}
                min={assignStartDate || today}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetAssignForm();
                setAssignDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAssign}
              loading={createPlacement.isPending}
              disabled={
                createPlacement.isPending ||
                !assignStudent ||
                !assignPartner ||
                !assignSlot ||
                !assignHours ||
                !assignStartDate
              }
            >
              {createPlacement.isPending ? "Authorizing..." : "Authorize assignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Placement detail drawer */}
      <Sheet
        open={detailPlacementId !== null}
        onOpenChange={(o) => !o && setDetailPlacementId(null)}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[460px]"
          aria-describedby={undefined}
        >
          <SheetHeader className="space-y-1 border-b border-border px-6 py-5 text-left">
            <p className="eyebrow">Placement detail</p>
            <div className="flex flex-wrap items-center gap-2 pr-6">
              <SheetTitle className="text-xl font-normal">
                {detailLoading ? "Loading..." : detailStudent.label}
              </SheetTitle>
              {detailStudent.incomplete && !detailLoading && (
                <Badge variant="warning">Incomplete profile</Badge>
              )}
            </div>
            <SheetDescription className="text-body">
              {detail?.partnerName ?? detail?.partner_name ?? ""}
            </SheetDescription>
            {detailStudent.email && (
              <p className="text-caption text-muted-foreground">{detailStudent.email}</p>
            )}

            {detail && (
              <div className="pt-3">
                <div className="mb-1.5 flex justify-between text-caption">
                  <span className="text-muted-foreground">Deployment progress</span>
                  <span className="font-mono text-foreground tabular-nums">
                    {Math.round(detailProgress)}%
                  </span>
                </div>
                <Progress value={detailProgress} aria-label="Deployment progress" />
                <p className="mt-1.5 text-caption text-muted-foreground tabular-nums">
                  {detailLogged} / {detailRequired} approved hours
                  {detailPending > 0 ? ` · ${detailPending}h awaiting review` : ""}
                </p>
              </div>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {detailLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : !detail ? (
              <p className="text-sm text-muted-foreground">
                This placement could not be loaded. It may have been terminated.
              </p>
            ) : (
              <div className="space-y-5">
                <OpsDetailSection label="Intern">
                  <p className="text-sm text-foreground">
                    {detail.studentEmail ?? detail.student_email ?? "—"}
                  </p>
                  {(detail.university || detail.major || detail.yearOfStudy) && (
                    <p className="text-sm text-muted-foreground">
                      {[
                        detail.university,
                        detail.major,
                        detail.yearOfStudy ? `Year ${detail.yearOfStudy}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </OpsDetailSection>

                <Separator />

                <OpsDetailSection label="Corporate host">
                  <p className="text-sm font-medium text-foreground">
                    {detail.partnerName ?? detail.partner_name ?? "—"}
                  </p>
                  {(detail.partnerIndustry || detail.partnerLocation) && (
                    <p className="text-sm text-muted-foreground">
                      {[
                        detail.partnerIndustry ?? detail.partner_industry,
                        detail.partnerLocation ?? detail.partner_location,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </OpsDetailSection>

                <Separator />

                <OpsDetailSection label="Assignment">
                  <p className="text-sm font-medium text-foreground">{detail.slot ?? "—"}</p>
                  {(detail.slotLocation || detail.slotArrangement || detail.slotDuration) && (
                    <p className="text-sm text-muted-foreground">
                      {[
                        detail.slotLocation ?? detail.slot_location,
                        detail.slotArrangement ?? detail.slot_arrangement,
                        detail.slotDuration ?? detail.slot_duration,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                  {(detail.startDate || detail.start_date) && (
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {formatDate(detail.startDate ?? detail.start_date)}
                      {(detail.endDate ?? detail.end_date)
                        ? ` – ${formatDate(detail.endDate ?? detail.end_date)}`
                        : " – ongoing"}
                    </p>
                  )}
                  {(detail.slotDescription || detail.slot_description) && (
                    <p className="pt-1 text-sm text-body">
                      {detail.slotDescription ?? detail.slot_description}
                    </p>
                  )}
                  <div className="pt-2">
                    <StatusChip status={detail.status} />
                  </div>
                </OpsDetailSection>

                <Separator />

                <OpsDetailSection label={`Work logs (${detailLogs.length})`}>
                  {detailLogs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No work logs submitted yet.</p>
                  ) : (
                    <ul className="space-y-2 pt-1">
                      {detailLogs.map((log: any) => (
                        <li key={log.id} className="rounded-md border border-border bg-card p-3">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <span className="text-caption text-muted-foreground tabular-nums">
                              {formatDate(log.date)}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-caption text-foreground tabular-nums">
                                {log.hours}h
                              </span>
                              <StatusChip status={log.status} />
                            </div>
                          </div>
                          <p className="text-sm text-foreground">{log.content}</p>
                          {log.feedback && (
                            <p className="mt-1.5 text-caption text-muted-foreground">
                              Reviewer: {log.feedback}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </OpsDetailSection>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
